import { ListLoadError } from '../../components/list-load-error';
import {
  ActionBar,
  type ActionBarAction,
  Button,
  Card,
  EmptyState,
  PageHeader,
  ResponsiveList,
  Skeleton,
  StatusBadge,
  todayInArgentina,
  useMediaQuery,
  useToast,
} from '@catering/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import { ClipboardList, Download, FileClock, FileSearch, Plus, Search } from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { OperatingUnitSelect } from '../../components/operating-unit-select';
import { listSites, searchAllCustomers } from '../../customers/api';
import { GuideLink } from '../../guide/guide-link';
import { formatCalendarDate } from '../../orders/format-calendar-date';
import {
  exportOrders,
  listOrders,
  ORDER_SOURCE_LABELS,
  type Order,
  type OrderSource,
  type OrderStatus,
} from '../../orders/api';
import { useOperatingUnitOptions } from '../../operating-units/use-operating-unit-options';
import { hasPermission } from '../../auth/has-permission';

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

const ordersRoute = getRouteApi('/protected/orders');

const STATUS_LABELS: Record<OrderStatus, string> = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviado',
  CONFIRMED: 'Confirmado',
  CANCELLED: 'Cancelado',
};

interface FilterPatch {
  serviceDate?: string | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  status?: OrderStatus | undefined;
  source?: OrderSource | undefined;
}

/**
 * `orders/spec.md` → "Listado de pedidos con filtros". `page`/`pageSize`
 * live in the URL, same convention as `customerListSearchSchema`; unlike
 * `/customers`, `pageSize` is itself a URL param, not a fixed constant —
 * `listOrders` clamps it to `MAX_ORDER_PAGE_SIZE` before the request ever
 * leaves the browser (`orders-module/tasks.md` PR 5 TRIANGULATE: a
 * hand-edited `?pageSize=500` must not reach the API as-is).
 */
export function OrderListPage(): JSX.Element {
  const search = ordersRoute.useSearch();
  const navigate = ordersRoute.useNavigate();
  const appNavigate = useNavigate();
  const currentUser = useCurrentUser();
  const { toast } = useToast();
  const isDesktop = useMediaQuery('(min-width: 640px)');

  // The route's URL search params live in `router.tsx`; this filter stays local state.
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const showUnit = useOperatingUnitOptions().length > 1;
  const unitFilter = operatingUnitId === '' ? undefined : operatingUnitId;

  const query = useQuery({
    queryKey: [
      'orders',
      search.serviceDate,
      search.customerId,
      search.siteId,
      operatingUnitId,
      search.status,
      search.source,
      search.page,
      search.pageSize,
    ],
    queryFn: () =>
      listOrders({
        serviceDate: search.serviceDate,
        customerId: search.customerId,
        siteId: search.siteId,
        operatingUnitId: unitFilter,
        status: search.status,
        source: search.source,
        page: search.page,
        pageSize: search.pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  const customersQuery = useQuery({
    queryKey: ['customers-for-orders'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const sitesQuery = useQuery({
    queryKey: ['sites-for-orders', search.customerId],
    queryFn: () => listSites(search.customerId ?? ''),
    enabled: search.customerId !== undefined,
  });

  const customers = customersQuery.data?.items ?? [];
  const customerNames = Object.fromEntries(
    customers.map((customer) => [customer.id, customer.legalName]),
  );
  const sites = sitesQuery.data ?? [];

  const canCreate = hasPermission(currentUser.data, 'order.create');
  const canOverride = hasPermission(currentUser.data, 'order.override.cutoff');
  const canManageTemplates = hasPermission(currentUser.data, 'order.template.manage');
  const canApproveChanges = hasPermission(currentUser.data, 'order.approve.change');

  function applyFilters(patch: FilterPatch): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  }

  function goToPage(page: number): void {
    void navigate({ search: (prev) => ({ ...prev, page }) });
  }

  function changePageSize(pageSize: number): void {
    void navigate({ search: (prev) => ({ ...prev, pageSize, page: 1 }) });
  }

  async function handleExport(): Promise<void> {
    try {
      await exportOrders({
        serviceDate: search.serviceDate,
        customerId: search.customerId,
        siteId: search.siteId,
        operatingUnitId: unitFilter,
        status: search.status,
        source: search.source,
      });
    } catch {
      toast('error', 'No pudimos exportar el listado.');
    }
  }

  const hasActiveFilters =
    search.customerId !== undefined ||
    search.siteId !== undefined ||
    search.status !== undefined ||
    search.source !== undefined ||
    operatingUnitId !== '';

  const hasChangedFilters = hasActiveFilters || search.serviceDate !== todayInArgentina();

  function clearFilters(): void {
    setOperatingUnitId('');
    void navigate({
      search: (prev) => ({
        serviceDate: todayInArgentina(),
        page: 1,
        pageSize: prev.pageSize,
      }),
    });
  }

  const headerActions: ActionBarAction[] = [
    ...(canCreate
      ? [{ label: 'Nuevo pedido', onClick: () => void appNavigate({ to: '/orders/new' }) }]
      : []),
    { label: 'Exportar CSV', icon: Download, onClick: () => void handleExport() },
    ...(canManageTemplates
      ? [
          {
            label: 'Plantillas',
            icon: FileClock,
            onClick: () => void appNavigate({ to: '/orders/templates' }),
          },
        ]
      : []),
    ...(canApproveChanges
      ? [
          {
            label: 'Solicitudes de cambio',
            icon: FileSearch,
            onClick: () => void appNavigate({ to: '/orders/change-requests' }),
          },
        ]
      : []),
  ];
  const primaryAction = headerActions[0];
  const secondaryActions = headerActions.slice(1);

  const totalPages =
    query.data === undefined ? 1 : Math.max(1, Math.ceil(query.data.total / search.pageSize));

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={ClipboardList}
        title="Pedidos"
        description="Consultá y gestioná la carga diaria de pedidos internos."
        actions={
          isDesktop ? (
            <div className="flex items-center gap-2">
              {canManageTemplates ? (
                <Link to="/orders/templates">
                  <Button variant="secondary" icon={FileClock}>
                    Plantillas
                  </Button>
                </Link>
              ) : null}
              {canApproveChanges ? (
                <Link to="/orders/change-requests">
                  <Button variant="secondary" icon={FileSearch}>
                    Solicitudes de cambio
                  </Button>
                </Link>
              ) : null}
              <Button variant="secondary" icon={Download} onClick={() => void handleExport()}>
                Exportar CSV
              </Button>
              {canCreate ? (
                <Link to="/orders/new">
                  <Button icon={Plus}>Nuevo pedido</Button>
                </Link>
              ) : null}
              <GuideLink moduleId="orders" />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {primaryAction === undefined ? null : (
                <ActionBar primary={primaryAction} secondary={secondaryActions} />
              )}
              <GuideLink moduleId="orders" />
            </div>
          )
        }
      />

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Fecha de servicio</span>
            <input
              type="date"
              value={search.serviceDate ?? ''}
              onChange={(event) =>
                applyFilters({
                  serviceDate: event.target.value === '' ? undefined : event.target.value,
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Empresa</span>
            <select
              value={search.customerId ?? ''}
              onChange={(event) =>
                applyFilters({
                  customerId: event.target.value === '' ? undefined : event.target.value,
                  siteId: undefined,
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todas</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.legalName}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Sede</span>
            <select
              value={search.siteId ?? ''}
              disabled={search.customerId === undefined}
              onChange={(event) =>
                applyFilters({ siteId: event.target.value === '' ? undefined : event.target.value })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50"
            >
              <option value="">Todas</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>

          <OperatingUnitSelect
            id="order-operating-unit-filter"
            label="Punto de venta"
            allLabel="Todos los puntos de venta"
            value={operatingUnitId}
            onChange={(value) => {
              setOperatingUnitId(value);
              goToPage(1);
            }}
          />

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Estado</span>
            <select
              value={search.status ?? ''}
              onChange={(event) =>
                applyFilters({
                  status:
                    event.target.value === '' ? undefined : (event.target.value as OrderStatus),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {(Object.keys(STATUS_LABELS) as OrderStatus[]).map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Origen</span>
            <select
              value={search.source ?? ''}
              onChange={(event) =>
                applyFilters({
                  source:
                    event.target.value === '' ? undefined : (event.target.value as OrderSource),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {(Object.keys(ORDER_SOURCE_LABELS) as OrderSource[]).map((source) => (
                <option key={source} value={source}>
                  {ORDER_SOURCE_LABELS[source]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {hasChangedFilters ? (
          <div className="mt-4">
            <Button variant="secondary" size="sm" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        ) : null}
      </Card>

      {query.isError ? (
        <ListLoadError
          title="No pudimos cargar los pedidos"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton data-testid="orders-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="orders-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="orders-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="orders-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="orders-skeleton-row" className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && query.data.items.length === 0 ? (
        <EmptyState
          icon={hasActiveFilters ? Search : ClipboardList}
          title={
            hasActiveFilters ? 'No se encontraron pedidos.' : 'Todavía no hay pedidos cargados.'
          }
        />
      ) : null}

      {query.data !== undefined && query.data.items.length > 0 ? (
        <>
          <ResponsiveList
            items={query.data.items}
            rowKey={(order) => order.id}
            caption="Pedidos"
            getRowTestId={(order) => `order-card-${order.id}`}
            emptyState={null}
            columns={[
              {
                key: 'orderNumber',
                header: 'Número',
                render: (order) => (
                  <>
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: order.id }}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {order.orderNumber}
                    </Link>
                    {order.cutoffOverridden && canOverride ? (
                      <span className="ml-2 text-xs font-medium text-amber-700">
                        Excepción de corte
                      </span>
                    ) : null}
                  </>
                ),
              },
              {
                key: 'customer',
                header: 'Empresa',
                render: (order) => customerNames[order.customerId] ?? order.customerName,
              },
              { key: 'site', header: 'Sede', render: (order) => order.siteName },
              ...(showUnit
                ? [
                    {
                      key: 'operatingUnit',
                      header: 'Punto de venta',
                      render: (order: Order) => order.operatingUnitName ?? '—',
                    },
                  ]
                : []),
              {
                key: 'serviceDate',
                header: 'Fecha de servicio',
                render: (order) => formatCalendarDate(order.serviceDate),
              },
              {
                key: 'status',
                header: 'Estado',
                render: (order) => <StatusBadge domain="order" status={order.status} />,
              },
              { key: 'quantity', header: 'Cantidad', render: (order) => order.totalQuantity },
            ]}
            renderCardHeader={(order) => (
              <Link
                to="/orders/$orderId"
                params={{ orderId: order.id }}
                className="flex items-center justify-between gap-2"
              >
                <span>{order.orderNumber}</span>
                <StatusBadge domain="order" status={order.status} />
              </Link>
            )}
            renderCardBody={(order) => (
              <Link
                to="/orders/$orderId"
                params={{ orderId: order.id }}
                className="col-span-2 grid grid-cols-2 gap-2"
              >
                <span>{customerNames[order.customerId] ?? order.customerName}</span>
                <span>{order.siteName}</span>
                {showUnit ? <span>{order.operatingUnitName ?? '—'}</span> : null}
                <span>{formatCalendarDate(order.serviceDate)}</span>
                <span>{order.totalQuantity} unidades</span>
                {order.cutoffOverridden && canOverride ? (
                  <span className="col-span-2 text-xs font-medium text-amber-700">
                    Excepción de corte
                  </span>
                ) : null}
              </Link>
            )}
          />

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              {query.data.total} {query.data.total === 1 ? 'pedido' : 'pedidos'} en total
            </span>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2">
                <span>Por página</span>
                <select
                  value={search.pageSize}
                  onChange={(event) => changePageSize(Math.min(Number(event.target.value), 100))}
                  className="h-9 rounded-md border border-slate-300 px-2 text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page <= 1}
                onClick={() => goToPage(search.page - 1)}
              >
                Anterior
              </Button>
              <span>
                Página {search.page} de {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page >= totalPages}
                onClick={() => goToPage(search.page + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
